import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { acknowledgeSavedFinanceRows, getChangedFinanceRows, getRowsEditSnapshot, mergeFinanceRowBaselines } from '../utils/financeReconciliationRows';

const useFinanceReconciliationAutoSave = ({
  id, rows, setRows, baselineRows, setBaselineRows, canCreate, canEdit, enabled, blocked,
  saveReportRows, onSaved, onError, getRowLabel
}) => {
  const [saving, setSaving] = useState(false);
  const [failedSnapshot, setFailedSnapshot] = useState('');
  const [retryProviderIds, setRetryProviderIds] = useState([]);
  const requestRef = useRef(null);
  const rowsSnapshot = useMemo(() => getRowsEditSnapshot(rows), [rows]);
  const baselineSnapshot = useMemo(() => getRowsEditSnapshot(baselineRows), [baselineRows]);
  const hasPendingChanges = rowsSnapshot !== baselineSnapshot || retryProviderIds.length > 0;

  useEffect(() => {
    setSaving(false);
    setFailedSnapshot('');
    setRetryProviderIds([]);
    return () => { requestRef.current = null; };
  }, [id]);

  const saveRows = useCallback(async (automatic = false) => {
    if (requestRef.current || !enabled || blocked) return;

    const editableRows = rows.filter(row => row.has_report ? canEdit : canCreate);
    const changedIds = new Set(getChangedFinanceRows(editableRows, baselineRows).map(row => String(row.master_provider_id)));
    const rowsToSave = automatic
      ? editableRows.filter(row => changedIds.has(String(row.master_provider_id)) || retryProviderIds.includes(String(row.master_provider_id)))
      : editableRows;
    if (rowsToSave.length === 0) {
      if (!automatic) onError('No editable provider rows to save');
      return;
    }

    const invalidRow = rowsToSave.find(row => (
      !Number.isFinite(Number(row.bo_winlose_amount || 0)) ||
      !Number.isFinite(Number(row.provider_winlose_amount || 0))
    ));
    if (invalidRow) {
      onError(`${getRowLabel(invalidRow)} has invalid amount`);
      setFailedSnapshot(rowsSnapshot);
      return;
    }

    const request = {};
    requestRef.current = request;
    setSaving(true);
    onError(null);

    try {
      const res = await saveReportRows(id, {
        rows: rowsToSave.map(row => ({
          master_provider_id: row.master_provider_id,
          bo_turnover_amount: row.bo_turnover_amount,
          bo_valid_turnover_amount: row.bo_valid_turnover_amount,
          bo_winlose_amount: row.bo_winlose_amount,
          provider_winlose_amount: row.provider_winlose_amount,
          notes: row.notes
        }))
      });
      if (requestRef.current !== request) return;
      if (!res.success) throw new Error(res.message || 'Failed to save provider rows');

      setRows(currentRows => acknowledgeSavedFinanceRows(currentRows, rowsToSave));
      setBaselineRows(currentRows => mergeFinanceRowBaselines(currentRows, rowsToSave));
      setFailedSnapshot('');
      setRetryProviderIds([]);
      onSaved(res, automatic);
    } catch (err) {
      if (requestRef.current !== request) return;
      // A failed response may follow a partial write. Retry these rows even if
      // the user has since restored their values to the previous baseline.
      setRetryProviderIds(rowsToSave.map(row => String(row.master_provider_id)));
      setFailedSnapshot(rowsSnapshot);
      onError(`${automatic ? 'Auto-save failed. Use Save All to retry. ' : ''}${err.message || 'Failed to save provider rows'}`);
    } finally {
      if (requestRef.current === request) {
        requestRef.current = null;
        setSaving(false);
      }
    }
  }, [baselineRows, blocked, canCreate, canEdit, enabled, getRowLabel, id, onError, onSaved, retryProviderIds, rows, rowsSnapshot, saveReportRows, setBaselineRows, setRows]);

  useEffect(() => {
    if (!hasPendingChanges || !enabled || blocked || saving || failedSnapshot === rowsSnapshot) return undefined;
    const timer = setTimeout(() => saveRows(true), 1000);
    return () => clearTimeout(timer);
  }, [blocked, enabled, failedSnapshot, hasPendingChanges, rowsSnapshot, saveRows, saving]);

  return {
    saving,
    saveRows,
    autoSaveFailed: failedSnapshot === rowsSnapshot,
    // Restoring the old value cannot make an outstanding write safe to leave.
    hasUnsavedChanges: hasPendingChanges || saving
  };
};

export default useFinanceReconciliationAutoSave;
